import { jsx as _jsx } from "react/jsx-runtime";

/* hs-eslint ignored failing-rules */
import noop from 'hs-lodash/noop';
import I18n from 'I18n';
import { MobileDropdown } from 'signup-ui-full-screen-search/components/MobileDropdown';
import { stripDiacritics } from 'UIComponents/utils/Diacritics';
import { useAutofocus } from '../hooks/useAutofocus';
import { useJobRoles } from '../hooks/useJobRoles';
import UISelect from 'UIComponents/input/UISelect';
import JobRoleService from '../services/JobRoleService';
export const getJobRoleOtherOption = () => ({
  id: 'other',
  text: I18n.text('jobRoleTypeahead.otherOption.text'),
  help: I18n.text('jobRoleTypeahead.otherOption.help'),
  category: 'other',
  value: 'other'
});

const getSortedMatchedJobRoles = (searchValue, jobRoleOptions) => {
  const jobRoleMatch = jobRoleOptions.filter(option => {
    return stripDiacritics(option.text.toLowerCase()).includes(searchValue);
  });
  return jobRoleMatch.sort((a, b) => {
    const aStart = a.text.toLowerCase().indexOf(searchValue);
    const bStart = b.text.toLowerCase().indexOf(searchValue);

    if (aStart === bStart) {
      return a.text.length - b.text.length;
    }

    return aStart - bStart;
  });
};

const getMatchingOptions = (valueToFilter, jobRoles, shouldUseRelevantSearch) => {
  let matchingOptions;

  if (shouldUseRelevantSearch) {
    matchingOptions = getSortedMatchedJobRoles(valueToFilter.toLowerCase(), jobRoles);
  } else {
    matchingOptions = jobRoles.filter(jobRole => stripDiacritics(jobRole.text.toLowerCase()).includes(valueToFilter));
  }

  if (matchingOptions.length === 0) {
    matchingOptions.push(getJobRoleOtherOption());
  }

  return matchingOptions;
};
/**
 * Load options given on a value to filter.
 * This is intented to be used as the loadOptions property
 * on the MobileDropdown component
 *
 * @param {String} valueToFilter The value to filter.
 * @returns {Promise}
 */


const loadOptions = valueToFilter => {
  return new Promise(resolve => {
    if (!valueToFilter.length) {
      resolve(null);
    }

    const shouldUseRelevantSearch = JobRoleService.state.shouldUseRelevantSearch;
    JobRoleService.all().then(jobRoles => {
      resolve({
        options: getMatchingOptions(valueToFilter, jobRoles, shouldUseRelevantSearch)
      });
    }).catch(() => {
      resolve({
        options: getMatchingOptions(valueToFilter, [], shouldUseRelevantSearch)
      });
    });
  });
};
/**
 * Job Role input.
 *
 * This component will use the getMatchingJobRoles method to get a list
 * with all possible job roles that can be selected by the user.
 * Typing on the select input will filter the full list of job roles as a normal UISelect
 * component. The filtering/matching is done by the getMatchingJobRoles method as the
 * filterOptions is set to false on the UISelect.
 *
 */


export const JobRoleTypeahead = ({
  inputValue = '',
  seleniumSelector = 'jobrole',
  isMobile = false,
  placeholder = I18n.text('jobRoleTypeahead.placeholder'),
  autoFocus = true,
  focusTimeout = 0,
  onInputValueChange = noop,
  onSelectOptionChange = noop,
  shouldUseRelevantSearch = false,
  shouldShowPlaceholder = true
}) => {
  const {
    list: jobRoles,
    isLoading
  } = useJobRoles();
  const {
    inputFieldRef
  } = useAutofocus({
    autoFocus,
    focusTimeout
  });
  JobRoleService.setShouldUseRelevantSearch(shouldUseRelevantSearch);
  const seleniumKey = 'job-role';

  if (isMobile) {
    return /*#__PURE__*/_jsx(MobileDropdown, Object.assign({
      illustrationCaption: 'jobRoleTypeahead.typeToSearch',
      illustrationName: 'dedicated-ip-briefcase',
      illustrationWidth: 100,
      noMatchesOption: getJobRoleOtherOption(),
      options: !shouldUseRelevantSearch ? jobRoles : undefined,
      loadOptions: shouldUseRelevantSearch ? loadOptions : undefined
    }, shouldShowPlaceholder && {
      searchPlaceholder: placeholder
    }, {
      selected: inputValue,
      seleniumKey: seleniumKey,
      showOptionsWhenNullSearchValue: false,
      onInputChange: onInputValueChange,
      onSelectChange: onSelectOptionChange
    }));
  }

  return /*#__PURE__*/_jsx(UISelect, Object.assign({
    anchorType: 'input',
    buttonUse: 'form',
    "data-selenium": seleniumSelector,
    filterOptions: false,
    inputRef: inputFieldRef,
    inputValue: inputValue,
    isLoading: isLoading,
    loadOptions: loadOptions
  }, shouldShowPlaceholder && {
    placeholder
  }, {
    onInputValueChange: event => {
      onInputValueChange(event.target.value);
    },
    onSelectedOptionChange: event => {
      onSelectOptionChange(event.target.value);
    }
  }));
};
export default JobRoleTypeahead;